import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  FileSpreadsheet, Package, CheckCircle2, AlertTriangle, Clock, ChevronRight,
  RefreshCw, ClipboardList, Receipt, FileDown, XCircle, Loader2,
  Settings, TimerOff, Undo2, Trash2, Repeat,
} from 'lucide-react';
import PickListsTab from './orders/PickListsTab';
import BulkOrderUploadTab from './orders/BulkOrderUploadTab';
import ProcurementDashboardTab from './orders/ProcurementDashboardTab';
import OrderDetailDialog from './orders/OrderDetailDialog';
import {
  StatusBadge, SourceBadge, STATUS_LABELS, CANCELLABLE_STATUSES,
} from './orders/_status.jsx';
import { authHeaders } from '@/lib/authToken';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// =======================================================================
// Dashboard cards
// =======================================================================
function DashboardTile({ tile }) {
  const Icon = tile.icon;
  return (
    <Card className={`hover:shadow-md transition-shadow ${tile.onClick ? 'cursor-pointer' : ''}`}
      onClick={tile.onClick}
      data-testid={tile.testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={'p-2 rounded-lg bg-slate-50 ' + tile.color}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{tile.label}</p>
          <p className={'text-2xl font-bold ' + tile.color}>{tile.value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ExpiryConfigDialog({ open, onClose, currentDays, onSave }) {
  const [draft, setDraft] = useState(String(currentDays || 7));
  useEffect(() => {
    if (open) setDraft(String(currentDays || 7));
  }, [open, currentDays]);
  const presets = [3, 5, 7, 15, 30, 60];
  const parsed = parseInt(draft, 10);
  const valid = !Number.isNaN(parsed) && parsed >= 1 && parsed <= 60;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="expiry-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-orange-600" />Auto-Expiry Cut-off
          </DialogTitle>
          <DialogDescription>
            Order lines older than this many days will be auto-cancelled by the daily sweep.
            <span className="block mt-1 text-[11px] text-slate-500">Allowed range: 1 – 60 days.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <p className="text-xs uppercase font-semibold text-slate-500 mb-1.5">Quick Select</p>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDraft(String(d))}
                  data-testid={`expiry-preset-${d}`}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    parsed === d
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-orange-50'
                  }`}
                >
                  {d} {d === 1 ? 'day' : 'days'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase font-semibold text-slate-500 mb-1.5">Custom Days</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={60}
                step={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && valid) onSave(parsed); }}
                className="w-24 text-center"
                data-testid="expiry-custom-input"
                autoFocus
              />
              <span className="text-sm text-slate-600">days</span>
            </div>
            {!valid && draft !== '' && (
              <p className="text-xs text-red-600 mt-1">Enter a whole number 1 – 60.</p>
            )}
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
            Current setting: <span className="font-semibold text-slate-700">{currentDays || 7} days</span>
            {valid && parsed !== currentDays && (
              <> · New: <span className="font-semibold text-orange-700">{parsed} days</span></>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="expiry-cancel-btn">Cancel</Button>
          <Button
            onClick={() => onSave(parsed)}
            disabled={!valid || parsed === currentDays}
            data-testid="expiry-save-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrdersDashboardCards({
  stats, onRefresh, onResetAllocations,
  onRunAutoExpire, onConfigExpiry, onDownloadExpiredReport,
  onRestoreAutoCancelled, onSyncCancelledPickLists, expiryDays,
  repeatedPendingCount, onRepeatedTileClick,
}) {
  // Line-level counts (preferred when backend ships them) — falls back to
  // order-level counts so we never render blank tiles.
  const lineCounts = stats.order_lines_by_status || {};
  const lineCount = (key) => (lineCounts[key]?.count ?? stats[key] ?? 0);
  const tiles = [
    { label: 'Total Orders', value: stats.total_orders ?? 0, icon: ClipboardList, color: 'text-slate-700' },
    { label: 'Pending Procurement (lines)', value: lineCount('pending_procurement'), icon: Clock, color: 'text-red-600' },
    { label: 'Repeated Unprocured (15d)', value: repeatedPendingCount ?? 0, icon: Repeat, color: 'text-red-700',
      onClick: onRepeatedTileClick, testId: 'repeated-unprocured-tile' },
    { label: 'Ready to Pick (lines)', value: lineCount('ready_for_pick'), icon: Package, color: 'text-emerald-600' },
    { label: 'Invoiced (lines)', value: lineCount('invoiced'), icon: Receipt, color: 'text-green-600' },
  ];
  return (
    <div className="space-y-2" data-testid="orders-dashboard-cards">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tiles.map(tile => <DashboardTile key={tile.label} tile={tile} />)}
      </div>
      <div className="text-xs text-slate-500 flex items-center justify-between border border-dashed rounded-lg px-3 py-2 bg-slate-50">
        <span>Click Refresh to re-run allocation. Use &quot;Reset Allocations&quot; only if stock is over-reserved (one-time cleanup). Auto-expiry runs daily at {expiryDays ? `${expiryDays}d` : '20d'} — clears stale lines up to QA Completed; only Invoiced is preserved.</span>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={onConfigExpiry}
            data-testid="expiry-config-btn"
            className="text-slate-700 hover:bg-slate-100"
            title={`Configure auto-expiry days (currently ${expiryDays || 20} days)`}>
            <Settings className="w-4 h-4 mr-1" />Expiry: {expiryDays || 20}d
          </Button>
          <Button variant="ghost" size="sm" onClick={onRunAutoExpire}
            data-testid="run-auto-expire-btn"
            className="text-orange-700 hover:bg-orange-50"
            title="Run auto-expiry sweep now (cancels lines older than configured days)">
            <TimerOff className="w-4 h-4 mr-1" />Run Auto-Expiry
          </Button>
          <Button variant="ghost" size="sm" onClick={onRestoreAutoCancelled}
            data-testid="restore-auto-cancelled-btn"
            className="text-emerald-700 hover:bg-emerald-50"
            title="Undo auto-cancellations from the last 8 days. Manual cancellations are NOT touched.">
            <Undo2 className="w-4 h-4 mr-1" />Restore Last 8d
          </Button>
          <Button variant="ghost" size="sm" onClick={onSyncCancelledPickLists}
            data-testid="sync-cancelled-pick-lists-btn"
            className="text-red-700 hover:bg-red-50"
            title="Repair stale pick lists: strip every cancelled order line from any still-open pick list and cancel empty pick lists.">
            <Trash2 className="w-4 h-4 mr-1" />Sync Pick Lists
          </Button>
          <Button variant="ghost" size="sm" onClick={onDownloadExpiredReport}
            data-testid="expired-report-btn"
            className="text-blue-700 hover:bg-blue-50"
            title="Download Excel report of all auto-expired order lines">
            <FileSpreadsheet className="w-4 h-4 mr-1" />Expired Report
          </Button>
          <CsvDownloadButton onDownload={onDownloadExpiredReport} testId="expired-report-csv" className="h-8 text-xs" />
          <Button variant="ghost" size="sm" onClick={onResetAllocations}
            data-testid="reset-allocations-btn"
            className="text-amber-700 hover:bg-amber-50"
            title="DANGER: wipes all open allocations and re-runs FIFO from scratch against actual batch stock. Use when total reserved > batches_total.">
            <AlertTriangle className="w-4 h-4 mr-1" />Reset Allocations
          </Button>
          <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

// Payment status badge — pending / paid / partial / refunded / settled.
function PaymentBadge({ status }) {
  const s = (status || 'pending').toLowerCase();
  const styles = {
    paid:      'bg-emerald-100 text-emerald-800 border-emerald-300',
    settled:   'bg-emerald-100 text-emerald-800 border-emerald-300',
    partial:   'bg-amber-100 text-amber-800 border-amber-300',
    refunded:  'bg-blue-100 text-blue-800 border-blue-300',
    pending:   'bg-slate-100 text-slate-700 border-slate-300',
  };
  const label = { paid: 'Paid', settled: 'Settled', partial: 'Partial',
                  refunded: 'Refunded', pending: 'Payment Pending' }[s] || s;
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${styles[s] || styles.pending}`}
      data-testid={`payment-badge-${s}`}>
      {label}
    </span>
  );
}

// =======================================================================
// Repeated Unprocured Orders (15d) — badge + previous-order dialog
// =======================================================================
function RepeatBadge({ count, onClick, uid }) {
  return (
    <button type="button"
      onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
      title="Same franchise ordered this SKU within the last 15 days while the previous order is still Pending Procurement. Click to view previous order(s)."
      data-testid={`repeat-badge-${uid || 'order'}`}>
      <Repeat className="w-3 h-3" />
      Repeat{count > 1 ? ` ×${count}` : ''}
    </button>
  );
}

function RepeatPrevDialog({ line, onClose }) {
  if (!line) return null;
  const prev = line.repeat_prev_orders || [];
  const fmt = (v) => v ? String(v).slice(0, 10) : '—';
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="repeat-prev-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Repeat className="w-4 h-4" />Repeated Unprocured SKU (15 days)
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-slate-700">{line.product_name}</span>
            <span className="font-mono text-xs ml-1">({line.product_sku})</span>
            {' '}was already ordered by <b>{line._franchise_name}</b> and the previous
            order line is still <b>Pending Procurement</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-red-600 text-white">
              <tr>
                <th className="px-3 py-2 text-left text-xs uppercase">Prev Order #</th>
                <th className="px-3 py-2 text-left text-xs uppercase">Order Date</th>
                <th className="px-3 py-2 text-right text-xs uppercase">Ordered</th>
                <th className="px-3 py-2 text-right text-xs uppercase">Pending Qty</th>
                <th className="px-3 py-2 text-left text-xs uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {prev.map((p) => (
                <tr key={p.order_id} className="border-t" data-testid={`repeat-prev-row-${p.order_number}`}>
                  <td className="px-3 py-2 font-mono text-xs text-blue-700">{p.order_number}</td>
                  <td className="px-3 py-2 text-xs">{fmt(p.created_at)}</td>
                  <td className="px-3 py-2 text-right">{p.quantity}</td>
                  <td className="px-3 py-2 text-right font-semibold text-red-700">{p.pending_qty}</td>
                  <td className="px-3 py-2"><StatusBadge status="pending_procurement" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500">
          New orders are never blocked — this is a procurement alert so the team can
          prioritise the SKU. Raise / expedite the PO to clear both lines together.
        </p>
      </DialogContent>
    </Dialog>
  );
}


// =======================================================================
// Order summary card (one card per order, in "All" view)
// =======================================================================
function OrderSummaryCard({ order, onOpen, onCancel, cancellingId, onSettle, settlingId }) {
  const items = order.items || [];
  const count = items.length;
  const fr = order.franchise_name;
  const fid = order.franchise_business_id;
  const canCancel = CANCELLABLE_STATUSES.has(order.status);
  const paymentStatus = (order.payment_status || 'pending').toLowerCase();
  const isOG = order.source === 'og_retailer_app';
  // Show "Settle" only for OG orders that are still pending payment. Other
  // sources (store portal, bulk upload) don't have a credit ledger on OG.
  const canSettle = isOG && (paymentStatus === 'pending' || paymentStatus === 'partial');
  // Order Value (Feb 2026): stamped by GET /api/orders. Format to Indian
  // rupee grouping so ₹1,23,456.78 renders correctly.
  const orderValue = Number(order.order_value || 0);
  const orderValueStr = orderValue.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const repeatCount = Number(order.repeat_pending_lines || 0);
  return (
    <Card className={`hover:shadow-md cursor-pointer transition-shadow ${repeatCount > 0 ? 'ring-1 ring-red-400 bg-red-50/40' : ''}`}
      data-testid={`order-card-${order.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="flex-1 min-w-0" onClick={() => onOpen(order)}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-blue-700">{order.order_number}</p>
            <StatusBadge status={order.status} />
            <SourceBadge source={order.source} />
            {isOG && <PaymentBadge status={paymentStatus} />}
            {repeatCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white"
                title={`${repeatCount} SKU line(s) re-ordered within 15 days while the previous order is still Pending Procurement`}
                data-testid={`order-repeat-badge-${order.order_number}`}>
                <Repeat className="w-3 h-3" />Repeat SKU{repeatCount > 1 ? ` ×${repeatCount}` : ''}
              </span>
            )}
          </div>
          <p className="text-sm mt-1 truncate">
            {fr} <span className="text-slate-400 font-mono">({fid})</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {count} line(s) · uploaded {order.created_at ? new Date(order.created_at).toLocaleDateString() : '—'}
            {order.paid_amount ? ` · paid ₹${Number(order.paid_amount).toLocaleString('en-IN')}` : ''}
          </p>
        </div>
        <div className="text-right shrink-0" data-testid={`order-value-${order.order_number}`}
             title={`Order Value: sum of Ordered Qty × Selling Price for each line (${count} line${count === 1 ? '' : 's'})`}>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Order Value</p>
          <p className="text-base font-bold text-slate-800 tabular-nums">₹{orderValueStr}</p>
        </div>
        {canSettle && (
          <Button size="sm" variant="outline"
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            onClick={(e) => { e.stopPropagation(); onSettle(order); }}
            disabled={settlingId === order.id}
            data-testid={`settle-order-${order.order_number}`}>
            {settlingId === order.id
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <CheckCircle2 className="w-3.5 h-3.5" />}
            <span className="ml-1">Mark Paid</span>
          </Button>
        )}
        {canCancel && (
          <Button size="sm" variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={(e) => { e.stopPropagation(); onCancel(order); }}
            disabled={cancellingId === order.id}
            data-testid={`cancel-order-${order.order_number}`}>
            {cancellingId === order.id
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <XCircle className="w-3.5 h-3.5" />}
            <span className="ml-1">Cancel</span>
          </Button>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
      </CardContent>
    </Card>
  );
}

// =======================================================================
// Flat line-item table (when a specific status filter is selected)
// =======================================================================
function FlatLineTable({ lines, status, onOpenOrder, onShowRepeat }) {
  const showReason = status === 'expired' || status === 'cancelled';
  if (lines.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-8 text-center border rounded-lg"
        data-testid="flat-lines-empty">
        No line items in {STATUS_LABELS[status] || status.replace(/_/g, ' ')}
      </p>
    );
  }
  return (
    <div className="border rounded-lg overflow-x-auto" data-testid={`flat-lines-${status}`}>
      <table className="w-full text-sm">
        <thead className="bg-slate-700 text-white">
          <tr>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Order #</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Franchise</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Source</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">SKU</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Product</th>
            <th className="px-3 py-2.5 text-right text-xs uppercase tracking-wide">Req</th>
            <th className="px-3 py-2.5 text-right text-xs uppercase tracking-wide">Alloc</th>
            <th className="px-3 py-2.5 text-right text-xs uppercase tracking-wide">Pending</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Linked PO</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Pick List</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Status</th>
            {showReason && (
              <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Reason</th>
            )}
          </tr>
        </thead>
        <tbody>
          {lines.map((ln) => {
            const isCancelled = ln.status === 'cancelled';
            const isRepeat = !isCancelled && !!ln.repeat_pending;
            return (
            <tr key={ln._uid}
              className={`border-t hover:bg-slate-50 cursor-pointer ${isCancelled ? 'bg-red-50 line-through text-slate-400' : ''} ${isRepeat ? 'bg-red-100/70 border-l-4 border-l-red-600 hover:bg-red-100' : ''}`}
              onClick={() => onOpenOrder(ln._order_id)}
              data-testid={`flat-line-${ln._uid}`}>
              <td className="px-3 py-2 font-mono text-xs text-blue-700">{ln._order_number}</td>
              <td className="px-3 py-2 text-sm">
                <div>{ln._franchise_name}</div>
                <div className="text-xs text-slate-400">{ln._franchise_business_id}</div>
              </td>
              <td className="px-3 py-2"><SourceBadge source={ln._source} /></td>
              <td className="px-3 py-2 font-mono text-xs">{ln.product_sku}</td>
              <td className="px-3 py-2">
                {ln.product_name}
                {isRepeat && (
                  <div className="mt-1">
                    <RepeatBadge uid={ln._uid}
                      count={(ln.repeat_prev_orders || []).length}
                      onClick={() => onShowRepeat && onShowRepeat(ln)} />
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right">{ln.quantity}</td>
              <td className="px-3 py-2 text-right text-emerald-700">{ln.allocated_qty || 0}</td>
              <td className="px-3 py-2 text-right text-amber-700">{ln.pending_qty || 0}</td>
              <td className="px-3 py-2 font-mono text-xs">{ln.linked_po_number || '—'}</td>
              <td className="px-3 py-2 font-mono text-xs">{ln.linked_pick_list_number || '—'}</td>
              <td className="px-3 py-2"><StatusBadge status={ln.expired_flag ? 'expired' : ln.status} /></td>
              {showReason && (
                <td className="px-3 py-2 text-xs text-orange-700"
                  data-testid={`expiry-reason-${ln._uid}`}>
                  {ln.cancellation_reason || '—'}
                  {ln.expired_at && (
                    <div className="text-[10px] text-slate-400">
                      {String(ln.expired_at).slice(0, 19).replace('T', ' ')}
                    </div>
                  )}
                </td>
              )}
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );
}

// =======================================================================
// Date preset helper (Feb 2026): Today / Yesterday / Last 7 Days / This Month
// / Last Month / Custom. Returns ISO date strings (YYYY-MM-DD).
// =======================================================================
function computeDatePreset(preset) {
  const today = new Date();
  const iso = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (preset === 'today') {
    const s = startOfDay(today);
    return { start: iso(s), end: iso(s) };
  }
  if (preset === 'yesterday') {
    const y = startOfDay(today);
    y.setDate(y.getDate() - 1);
    return { start: iso(y), end: iso(y) };
  }
  if (preset === 'last7') {
    const s = startOfDay(today);
    s.setDate(s.getDate() - 6);
    return { start: iso(s), end: iso(today) };
  }
  if (preset === 'this_month') {
    const s = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: iso(s), end: iso(today) };
  }
  if (preset === 'last_month') {
    const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const e = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: iso(s), end: iso(e) };
  }
  return { start: '', end: '' };
}

// =======================================================================
// Cancelled Orders table (Feb 2026): Historical view showing Order ID,
// Franchise, Order Date, Cancelled Date, Cancelled By, Reason.
// =======================================================================
function CancelledOrdersTable({ orders, onOpen }) {
  if (orders.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-8 text-center border rounded-lg"
        data-testid="cancelled-empty">No cancelled orders in the current filter.</p>
    );
  }
  return (
    <div className="border rounded-lg overflow-x-auto" data-testid="cancelled-orders-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-700 text-white">
          <tr>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Order #</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Franchise</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Order Date</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Cancelled Date</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Cancelled By</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Reason</th>
            <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Status</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => {
            const fmt = (v) => v ? String(v).slice(0, 19).replace('T', ' ') : '—';
            return (
              <tr key={o.id}
                className="border-t hover:bg-slate-50 cursor-pointer"
                onClick={() => onOpen(o)}
                data-testid={`cancelled-row-${o.order_number}`}>
                <td className="px-3 py-2 font-mono text-xs text-blue-700">{o.order_number}</td>
                <td className="px-3 py-2 text-sm">
                  <div>{o.franchise_name}</div>
                  <div className="text-xs text-slate-400">{o.franchise_business_id}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{fmt(o.created_at)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{fmt(o.cancelled_at)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {o.cancelled_by_name || o.cancelled_by || '—'}
                </td>
                <td className="px-3 py-2 text-xs text-orange-700 max-w-[280px] truncate"
                  title={o.cancellation_reason || ''}>
                  {o.cancellation_reason || '—'}
                </td>
                <td className="px-3 py-2"><StatusBadge status="cancelled" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =======================================================================
// Orders list tab (filter + cards or flat table)
// =======================================================================
function OrdersListTab({
  orders, loading, onOpen, onExportShortages, onExportXlsx, onCancel, cancellingId,
  onSettle, settlingId,
  startDate, endDate, sourceFilter,
  onStartDate, onEndDate, onSourceFilter, onClearDates,
  filter, setFilter,
}) {
  // Order Value sort (Feb 2026): 'newest' preserves the default created-desc
  // order from the API; 'value-desc' / 'value-asc' let admins pin the highest-
  // or lowest-value orders to the top without disturbing existing filters.
  const [sortBy, setSortBy] = useState('newest');
  // Franchise Orders enhancement (Feb 2026): Active vs Cancelled tabs +
  // Order ID / Franchise ID search + date presets. All state lives in the
  // tab component so it resets cleanly on remount without touching the
  // parent's date-range state (which is used for the API fetch).
  const [view, setView] = useState('active'); // 'active' | 'cancelled'
  const [orderIdQuery, setOrderIdQuery] = useState('');
  const [franchiseIdQuery, setFranchiseIdQuery] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  // Repeated Unprocured (15d): line clicked for the previous-order popup.
  const [repeatLine, setRepeatLine] = useState(null);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === 'all') {
      onStartDate('');
      onEndDate('');
      return;
    }
    if (preset === 'custom') return; // keep whatever the user types
    const { start, end } = computeDatePreset(preset);
    onStartDate(start);
    onEndDate(end);
  };

  const clearAllFilters = () => {
    setOrderIdQuery('');
    setFranchiseIdQuery('');
    setDatePreset('all');
    onClearDates();
    setFilter('all');
  };

  // Cancelled orders are surfaced ONLY in the Cancelled tab. Active view
  // hides them wholesale so operators can focus on live work.
  const isCancelledOrder = (o) => o.status === 'cancelled';
  const baseForView = view === 'cancelled'
    ? orders.filter(isCancelledOrder)
    : orders.filter(o => !isCancelledOrder(o));

  // Client-side text search — case-insensitive substring on order_number
  // (falls back to id) and franchise_business_id / franchise_id / name.
  const oidQ = orderIdQuery.trim().toLowerCase();
  const fidQ = franchiseIdQuery.trim().toLowerCase();
  const searchedOrders = baseForView.filter((o) => {
    if (oidQ) {
      const hay = `${o.order_number || ''} ${o.id || ''}`.toLowerCase();
      if (!hay.includes(oidQ)) return false;
    }
    if (fidQ) {
      const hay = `${o.franchise_business_id || ''} ${o.franchise_id || ''} ${o.franchise_name || ''}`.toLowerCase();
      if (!hay.includes(fidQ)) return false;
    }
    return true;
  });

  const isLineFilter = view === 'active' && filter !== 'all' && filter !== 'cancelled';
  const matchesFilter = (it) => {
    if (filter === 'expired') return !!it.expired_flag;
    // Defensive: never surface cancelled lines under any "open work" filter,
    // even if the backend race-condition leaves status='pending_procurement'
    // with pending_qty=0. Operators were confused by cancelled lines lingering
    // in the Pending Procurement tab — this guard removes the ambiguity.
    if (it.status === 'cancelled') return false;
    if (filter === 'repeated_pending') return !!it.repeat_pending;
    if (filter === 'pending_procurement') {
      return it.status === 'pending_procurement' && (it.pending_qty || 0) > 0;
    }
    return it.status === filter;
  };
  const ordersForCards = filter === 'all'
    ? searchedOrders
    : searchedOrders.filter(o =>
        o.status === filter
        || (o.items || []).some(matchesFilter)
      );
  // Apply Order Value sort AFTER filtering. `newest` = original API order
  // (already sorted by created_at desc); the value sorts are non-destructive
  // (create a copy — never mutate the parent's `orders` array).
  const sortedOrdersForCards = (() => {
    if (sortBy === 'value-desc') {
      return [...ordersForCards].sort((a, b) => Number(b.order_value || 0) - Number(a.order_value || 0));
    }
    if (sortBy === 'value-asc') {
      return [...ordersForCards].sort((a, b) => Number(a.order_value || 0) - Number(b.order_value || 0));
    }
    return ordersForCards;
  })();
  const flatLines = isLineFilter
    ? searchedOrders.flatMap(o =>
        (o.items || [])
          .filter(matchesFilter)
          .map(it => ({
            ...it,
            _order_id: o.id,
            _order_number: o.order_number,
            _franchise_name: o.franchise_name,
            _franchise_business_id: o.franchise_business_id,
            _source: o.source || 'bulk_upload',
            _uploaded_at: o.uploaded_at,
          })))
    : [];

  const activeCount = orders.filter(o => !isCancelledOrder(o)).length;
  const cancelledCount = orders.filter(isCancelledOrder).length;
  const summaryText = view === 'cancelled'
    ? `Showing ${searchedOrders.length} cancelled order${searchedOrders.length === 1 ? '' : 's'}`
    : (isLineFilter
        ? `Showing ${flatLines.length} line${flatLines.length === 1 ? '' : 's'}`
        : `Showing ${sortedOrdersForCards.length} order${sortedOrdersForCards.length === 1 ? '' : 's'}`);
  const hasAnyFilter = oidQ || fidQ || datePreset !== 'all' || startDate || endDate
    || sourceFilter !== 'all' || filter !== 'all';

  return (
    <div className="space-y-3" data-testid="orders-list-tab">
      {/* Active vs Cancelled tabs */}
      <div className="inline-flex rounded-lg border bg-slate-50 p-1" role="tablist"
        data-testid="orders-view-tabs">
        <button type="button"
          onClick={() => { setView('active'); setFilter('all'); }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'active'
              ? 'bg-white text-blue-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
          data-testid="orders-view-active">
          Active Orders <span className="ml-1 text-xs text-slate-400">({activeCount})</span>
        </button>
        <button type="button"
          onClick={() => { setView('cancelled'); setFilter('all'); }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'cancelled'
              ? 'bg-white text-red-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
          data-testid="orders-view-cancelled">
          Cancelled Orders <span className="ml-1 text-xs text-slate-400">({cancelledCount})</span>
        </button>
      </div>

      {/* Search + date preset bar */}
      <div className="flex items-end gap-3 flex-wrap p-3 bg-slate-50 border rounded-lg">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Order ID</label>
          <Input
            type="text"
            value={orderIdQuery}
            onChange={(e) => setOrderIdQuery(e.target.value)}
            placeholder="ORD-260706-0210"
            className="h-9 w-52 text-sm"
            data-testid="orders-search-order-id" />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Franchise ID</label>
          <Input
            type="text"
            value={franchiseIdQuery}
            onChange={(e) => setFranchiseIdQuery(e.target.value)}
            placeholder="FRN-FICO-001"
            className="h-9 w-48 text-sm"
            data-testid="orders-search-franchise-id" />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Date range</label>
          <select value={datePreset}
            onChange={(e) => handlePresetChange(e.target.value)}
            className="h-9 px-2 text-sm border rounded bg-white"
            data-testid="orders-date-preset">
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {datePreset === 'custom' && (
          <>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">From</label>
              <input type="date" value={startDate}
                onChange={(e) => onStartDate(e.target.value)}
                className="h-9 px-2 text-sm border rounded"
                data-testid="orders-start-date" />
            </div>
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">To</label>
              <input type="date" value={endDate}
                onChange={(e) => onEndDate(e.target.value)}
                className="h-9 px-2 text-sm border rounded"
                data-testid="orders-end-date" />
            </div>
          </>
        )}
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Source</label>
          <select value={sourceFilter}
            onChange={(e) => onSourceFilter(e.target.value)}
            className="h-9 px-2 text-sm border rounded bg-white"
            data-testid="orders-source-filter">
            <option value="all">All sources</option>
            <option value="store_portal">Store Portal</option>
            <option value="bulk_upload">Bulk Upload</option>
            <option value="og_retailer_app">OG Retailer App</option>
          </select>
        </div>
        <Button size="sm"
          variant={sourceFilter === 'og_retailer_app' ? 'default' : 'outline'}
          className={sourceFilter === 'og_retailer_app'
            ? 'bg-indigo-600 hover:bg-indigo-700 self-end'
            : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50 self-end'}
          onClick={() => onSourceFilter(sourceFilter === 'og_retailer_app' ? 'all' : 'og_retailer_app')}
          data-testid="filter-from-og-chip">
          {sourceFilter === 'og_retailer_app' ? '✓ ' : ''}From OG App
        </Button>
        {view === 'active' && (
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-9 px-2 text-sm border rounded bg-white"
              data-testid="orders-sort-filter"
            >
              <option value="newest">Newest first</option>
              <option value="value-desc">Order Value: High → Low</option>
              <option value="value-asc">Order Value: Low → High</option>
            </select>
          </div>
        )}
        {hasAnyFilter && (
          <Button size="sm" variant="ghost" onClick={clearAllFilters}
            data-testid="orders-clear-filters">Clear</Button>
        )}
        <div className="flex-1" />
        <p className="text-xs text-slate-500 self-center" data-testid="orders-result-count">
          {summaryText}
        </p>
      </div>

      {/* Status sub-filters — only meaningful in the Active tab */}
      {view === 'active' && (
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'pending_procurement', 'repeated_pending', 'ready_for_pick', 'picked', 'expired'].map(s => (
            <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'}
              onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={s === 'repeated_pending'
                ? (filter === s ? 'bg-red-600 hover:bg-red-700' : 'text-red-700 border-red-300 hover:bg-red-50')
                : undefined}>
              {s === 'all' ? 'All'
                : s === 'repeated_pending' ? (<><Repeat className="w-3.5 h-3.5 mr-1" />Repeated Pending (15d)</>)
                : (STATUS_LABELS[s] || s.replace(/_/g, ' '))}
            </Button>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={onExportXlsx} data-testid="export-orders-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" />Export Excel
          </Button>
          <Button size="sm" variant="outline" onClick={onExportShortages} data-testid="export-shortages-btn">
            <FileDown className="w-4 h-4 mr-1" />Export Shortages (CSV)
          </Button>
        </div>
      )}
      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {/* Cancelled tab: dedicated historical table */}
      {view === 'cancelled' && !loading && (
        <CancelledOrdersTable orders={searchedOrders} onOpen={onOpen} />
      )}

      {/* Active tab: card list or flat line-item table */}
      {view === 'active' && !loading && !isLineFilter && sortedOrdersForCards.length === 0 && (
        <p className="text-sm text-slate-500 py-8 text-center border rounded-lg">No orders found</p>
      )}
      {view === 'active' && !isLineFilter && sortedOrdersForCards.map(order => (
        <OrderSummaryCard key={order.id} order={order} onOpen={onOpen}
          onCancel={onCancel} cancellingId={cancellingId}
          onSettle={onSettle} settlingId={settlingId} />
      ))}

      {view === 'active' && isLineFilter && !loading && (
        <FlatLineTable lines={flatLines} status={filter}
          onShowRepeat={setRepeatLine}
          onOpenOrder={(orderId) => {
          const o = orders.find(x => x.id === orderId);
          if (o) onOpen(o);
        }} />
      )}

      {repeatLine && (
        <RepeatPrevDialog line={repeatLine} onClose={() => setRepeatLine(null)} />
      )}
    </div>
  );
}

// =======================================================================
// Main page
// =======================================================================
export default function Orders() {
  return <OrdersImpl />;
}

function OrdersImpl() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('orders');
  const [selected, setSelected] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancellingLineUid, setCancellingLineUid] = useState(null);
  const [settlingId, setSettlingId] = useState(null);
  const [settleDialog, setSettleDialog] = useState(null); // order being settled
  const [settleAmount, setSettleAmount] = useState('');
  const [settleNote, setSettleNote] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'store_portal' | 'bulk_upload' | 'og_retailer_app'
  const [listFilter, setListFilter] = useState('all'); // status sub-filter (lifted so KPI tile can set it)
  const [expiryDays, setExpiryDays] = useState(20);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      const [ord, ds] = await Promise.all([
        axios.get(`${API}/orders`, { headers: authHeaders(), params }),
        axios.get(`${API}/orders/dashboard`, { headers: authHeaders() }),
      ]);
      setOrders(ord.data || []);
      setStats(ds.data || {});
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, sourceFilter]);

  const resetAllocations = useCallback(async () => {
    const ok = window.confirm(
      'RESET ALL ALLOCATIONS?\n\n' +
      'This wipes every OPEN order line back to "pending_procurement", cancels all open pick lists, ' +
      'then re-runs FIFO allocation from scratch capped at actual batch stock.\n\n' +
      'Locked lines (picked, qa_pending, qa_completed) are NOT touched — those goods are already off-rack.\n\n' +
      'Use only if Total Reserved > Total Batch Stock for any product. Continue?'
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/admin/reset-allocations`, {},
        { headers: authHeaders() });
      const d = res.data || {};
      toast.success(
        `Reset complete: ${d.reset_lines || 0} line(s) on ${d.touched_orders || 0} order(s) re-evaluated. ` +
        `${d.locked_lines_skipped || 0} locked line(s) preserved.`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
    await fetchAll();
  }, [fetchAll]);

  // Explicit "re-run allocation" trigger — wired to the Refresh button so
  // users can force a sweep on demand. The read endpoints no longer run
  // allocation inline (it caused 30s timeouts on large datasets).
  const refreshWithReallocate = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/orders/reallocate`, {}, { headers: authHeaders() });
    } catch (err) {
      // Non-fatal — still fetch even if reallocation errored
    }
    await fetchAll();
  }, [fetchAll]);

  // Load configured expiry-days on mount so the dashboard button shows
  // the current value (3 / 5 / 7 / 15 etc).
  const loadExpiryConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/admin/expiry-config`,
        { headers: authHeaders() });
      setExpiryDays(res.data?.order_expiry_days || 20);
    } catch (err) {
      // Silent — non-admin users get a 403 here; default 7d is fine.
    }
  }, []);

  // Admin: trigger the auto-expiry sweep manually (also runs daily in bg).
  const runAutoExpire = useCallback(async () => {
    const ok = window.confirm(
      `Run Auto-Expiry Now?\n\n` +
      `This will auto-cancel the pending portion of every order line that is older than ${expiryDays} days ` +
      `AND still in any open state — Pending Procurement / Ready to Pick / Created / Allocated / QA Rejected / ` +
      `Picked / QA Pending / QA Completed.\n\n` +
      `Only INVOICED lines are preserved (those goods have already been billed and ledger-deducted).\n\n` +
      `Released stock (including from Picked / QA queues) is automatically re-flowed to the next FIFO-eligible order.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    setLoading(true);
    try {
      // Background mode (June 2026): the sweep can take 1–2 minutes on
      // a large production dataset, which exceeds the ingress timeout
      // and produces a misleading CORS error in the browser. We now
      // kick the job off in the background and poll a job-status
      // endpoint until completion.
      const res = await axios.post(`${API}/orders/admin/expire-stale?background=true`, {},
        { headers: authHeaders() });
      const jobId = res.data?.job_id;
      if (!jobId) {
        // Synchronous response (older backend) — handle as before.
        const d = res.data || {};
        if ((d.cancelled_lines || 0) > 0) {
          toast.success(
            `Auto-expiry complete: ${d.cancelled_lines} line(s) cancelled, ` +
            `${d.fully_cancelled_orders || 0} order(s) fully closed.`
          );
        } else {
          toast.info(`No stale lines found (cut-off = ${expiryDays} days).`);
        }
        await fetchAll();
        return;
      }
      toast.info(`Auto-expiry started in background (job ${jobId.slice(0, 8)}). Polling for completion…`);
      // Poll every 3s for up to 5 minutes.
      const deadline = Date.now() + 5 * 60 * 1000;
      let done = false;
      while (Date.now() < deadline && !done) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const s = await axios.get(`${API}/orders/admin/jobs/${jobId}`,
            { headers: authHeaders() });
          if (s.data?.status === 'completed') {
            const d = s.data.result || {};
            if ((d.cancelled_lines || 0) > 0) {
              toast.success(
                `Auto-expiry complete: ${d.cancelled_lines} line(s) cancelled, ` +
                `${d.fully_cancelled_orders || 0} order(s) fully closed. ` +
                `Reallocation upgraded ${d.reallocation_result?.upgraded_lines || 0} downstream line(s).`
              );
            } else {
              toast.info(`No stale lines found (cut-off = ${expiryDays} days).`);
            }
            done = true;
          } else if (s.data?.status === 'failed') {
            toast.error(`Auto-expiry job failed: ${s.data.error || 'unknown error'}`);
            done = true;
          }
        } catch (pollErr) {
          // Transient — keep polling.
        }
      }
      if (!done) {
        toast.warning('Auto-expiry is still running after 5 minutes. It will continue in the background; refresh the Orders list shortly.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Auto-expiry failed');
    }
    await fetchAll();
  }, [fetchAll, expiryDays]);

  // Admin: restore auto-cancelled lines on orders CREATED in the last
  // 8 days (one-shot safety net when the auto-expiry sweep was over-eager).
  // Filtering by order creation date — NOT cancel timestamp — so orders
  // that are genuinely stale (>8d old) stay cancelled regardless of when
  // the sweep ran.
  const restoreAutoCancelled = useCallback(async () => {
    const ok = window.confirm(
      `Restore Auto-Cancelled (orders created in last 8 days)?\n\n` +
      `This will UNDO auto-expired lines on orders whose creation date ` +
      `is within the last 8 days. Older orders stay cancelled even if ` +
      `they were cancelled by today's sweep.\n\n` +
      `Lines manually cancelled by users are NOT affected.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/orders/admin/restore-auto-cancelled?since_days=8`, {},
        { headers: authHeaders() }
      );
      const d = res.data || {};
      if ((d.restored_lines || 0) > 0) {
        toast.success(
          `Restored ${d.restored_lines} line(s) (${d.restored_qty} qty) across ` +
          `${d.affected_orders} order(s). ${d.skipped_stale_orders || 0} ` +
          `stale order(s) (>8d old) left cancelled. FIFO reallocation upgraded ` +
          `${d.reallocation?.upgraded_lines || 0} downstream line(s).`
        );
      } else {
        toast.info(
          `No restorable lines on orders created in the last 8 days. ` +
          `${d.skipped_stale_orders || 0} stale order(s) skipped.`
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
    await fetchAll();
  }, [fetchAll]);

  // Admin one-shot repair: re-runs the cancelled-line strip across every
  // order that has cancelled items. Use when historical sweeps left pick
  // lists / pending counts stale (e.g. legacy items missing `_uid`).
  const syncCancelledPickLists = useCallback(async () => {
    const ok = window.confirm(
      `Sync Pick Lists & Pending Counts?\n\n` +
      `This will scan EVERY order with cancelled lines and strip those ` +
      `lines from any still-open pick list. Empty pick lists will be ` +
      `auto-cancelled. Order data is NOT modified — only pick lists.\n\n` +
      `Safe to run multiple times. Continue?`
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/orders/admin/sync-cancelled-pick-lists`, {},
        { headers: authHeaders() }
      );
      const d = res.data || {};
      toast.success(
        `Scanned ${d.scanned_orders} order(s). Stripped ` +
        `${d.stripped_pick_list_items} stale pick-list item(s). ` +
        `${d.pick_lists_newly_emptied} pick list(s) auto-cancelled (empty).`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    }
    await fetchAll();
  }, [fetchAll]);
  // preset chips + a free-form 1-60 day input.
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const configureExpiry = useCallback(() => {
    setExpiryDialogOpen(true);
  }, []);

  const saveExpiryDays = useCallback(async (days) => {
    if (Number.isNaN(days) || days < 1 || days > 60) {
      toast.error('Please enter a whole number between 1 and 60');
      return;
    }
    try {
      const res = await axios.put(`${API}/orders/admin/expiry-config`,
        { order_expiry_days: days }, { headers: authHeaders() });
      setExpiryDays(res.data?.order_expiry_days || days);
      setExpiryDialogOpen(false);
      toast.success(`Auto-expiry set to ${days} days. Daily sweep will use this from next run.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update config');
    }
  }, []);

  // Admin: download Expired Orders Excel report.
  const downloadExpiredReport = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/orders/expired-report.xlsx`,
        { headers: authHeaders(), params, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `expired-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      toast.success('Expired Orders Report downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Expired report download failed');
    }
  }, [startDate, endDate]);

  useEffect(() => { loadExpiryConfig(); }, [loadExpiryConfig]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cancelOrder = useCallback(async (order) => {
    const reason = window.prompt(
      `Cancel order ${order.order_number}?\n\nThis releases reserved stock and cannot be undone.\nReason (optional):`,
      '',
    );
    if (reason === null) return;
    setCancellingId(order.id);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/cancel`,
        { reason }, { headers: authHeaders() });
      toast.success(`Order ${order.order_number} cancelled`);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? res.data : o)));
      setSelected((curr) => (curr && curr.id === order.id ? res.data : curr));
    } catch (err) {
      // Backend may return `detail` as a plain string (HTTPException) or as
      // a Pydantic-validation array (422). Flatten arrays/objects to a
      // readable string so the user sees the real reason, not "[object]".
      let msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map((d) => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else if (msg && typeof msg === 'object') {
        msg = msg.msg || msg.message || JSON.stringify(msg);
      }
      toast.error(msg || `Cancellation failed (HTTP ${err?.response?.status || '?'})`);
    } finally {
      setCancellingId(null);
    }
  }, []);

  const cancelLine = useCallback(async (line) => {
    const reason = window.prompt(
      `Cancel line "${line.product_name}" (qty ${line.quantity})?\n\nReleases this line's reserved stock back to inventory.\nReason (optional):`,
      '',
    );
    if (reason === null) return;
    setCancellingLineUid(line._uid);
    try {
      // Need the parent order id — pull from the currently-selected order.
      const currentOrder = selected;
      if (!currentOrder) return;
      const res = await axios.post(
        `${API}/orders/${currentOrder.id}/items/${line._uid}/cancel`,
        { reason }, { headers: authHeaders() });
      toast.success(`Line "${line.product_name}" cancelled`);
      setOrders((prev) => prev.map((o) => (o.id === res.data.id ? res.data : o)));
      setSelected(res.data);
    } catch (err) {
      let msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map((d) => d.msg || d.message || JSON.stringify(d)).join('; ');
      } else if (msg && typeof msg === 'object') {
        msg = msg.msg || msg.message || JSON.stringify(msg);
      }
      toast.error(msg || `Line cancellation failed (HTTP ${err?.response?.status || '?'})`);
    } finally {
      setCancellingLineUid(null);
    }
  }, [selected]);

  // ---- Payment settlement (OG Retailer App credit restoration) ----
  const openSettleDialog = useCallback((order) => {
    setSettleDialog(order);
    // Default the paid amount to the order's grand total when known.
    const def = order?.total
      ?? order?.grand_total
      ?? (order?.items || []).reduce((s, it) => s + (Number(it.total_price) || 0), 0);
    setSettleAmount(def ? String(Number(def).toFixed(2)) : '');
    setSettleNote('');
  }, []);

  const confirmSettle = useCallback(async () => {
    const order = settleDialog;
    if (!order) return;
    const amount = settleAmount.trim() === '' ? null : parseFloat(settleAmount);
    if (amount !== null && (Number.isNaN(amount) || amount < 0)) {
      toast.error('Enter a valid paid amount (₹0 or more)');
      return;
    }
    setSettlingId(order.id);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/settle`,
        { payment_status: 'paid',
          paid_amount: amount,
          note: settleNote || undefined },
        { headers: authHeaders() });
      const newOrder = res.data?.order;
      toast.success(`Order ${order.order_number} marked paid — OG App notified to restore credit`);
      if (newOrder) {
        setOrders((prev) => prev.map((o) => (o.id === newOrder.id ? newOrder : o)));
      }
      setSettleDialog(null);
    } catch (err) {
      let msg = err?.response?.data?.detail;
      if (Array.isArray(msg)) msg = msg.map((d) => d.msg || JSON.stringify(d)).join('; ');
      toast.error(msg || `Settle failed (HTTP ${err?.response?.status || '?'})`);
    } finally {
      setSettlingId(null);
    }
  }, [settleDialog, settleAmount, settleNote]);

  // Export dialog state — clicking "Export Excel" opens a date-range picker
  // pre-filled with the current table filter dates. The user can adjust the
  // window before downloading, so they never need to retro-fit the table
  // filters just to scope an export.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exportBusy, setExportBusy] = useState(false);

  const openExportDialog = () => {
    setExportStart(startDate || '');
    setExportEnd(endDate || '');
    setExportOpen(true);
  };

  const exportXlsx = async () => {
    setExportBusy(true);
    try {
      const params = new URLSearchParams();
      if (exportStart) params.set('start_date', exportStart);
      if (exportEnd) params.set('end_date', exportEnd);
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      const qs = params.toString();
      const res = await axios.get(
        `${API}/orders/export.xlsx${qs ? `?${qs}` : ''}`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      // Embed the chosen range in the filename so the operator can tell
      // exports apart on disk: orders-export-2026-06-01-to-2026-06-30.xlsx
      const stamp = (exportStart && exportEnd)
        ? `${exportStart}-to-${exportEnd}`
        : (exportStart || exportEnd || new Date().toISOString().slice(0, 10));
      a.download = `orders-export-${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(
        (exportStart || exportEnd)
          ? `Excel exported (${exportStart || '...'} → ${exportEnd || '...'})`
          : 'Excel exported (all dates)',
      );
      setExportOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Excel export failed');
    } finally {
      setExportBusy(false);
    }
  };

  const exportShortages = async () => {
    try {
      const res = await axios.get(`${API}/orders/shortages-export`,
        { headers: authHeaders() });
      const { total_rows, unmapped, csv } = res.data;
      if (!total_rows) {
        toast.info('No pending shortages to export');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `order-shortages-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success(`Exported ${total_rows} shortage line(s) — grouped by supplier`);
      if (unmapped > 0) {
        toast.warning(`${unmapped} line(s) had no supplier mapped — flagged in the CSV under "(UNMAPPED)". Add the brand under Suppliers → Brands Handled.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Shortages export failed');
    }
  };

  const onImported = () => { fetchAll(); setTab('orders'); };

  return (
    <div className="p-6 space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Order Management</h1>
          <p className="text-sm text-slate-600">
            Bulk upload → Auto allocate → Auto-PO → Pick → QA → Invoice
          </p>
        </div>
      </div>

      <OrdersDashboardCards
        stats={stats}
        repeatedPendingCount={orders.reduce((a, o) => a + Number(o.repeat_pending_lines || 0), 0)}
        onRepeatedTileClick={() => { setTab('orders'); setListFilter('repeated_pending'); }}
        onRefresh={refreshWithReallocate}
        onResetAllocations={resetAllocations}
        onRunAutoExpire={runAutoExpire}
        onConfigExpiry={configureExpiry}
        onDownloadExpiredReport={downloadExpiredReport}
        onRestoreAutoCancelled={restoreAutoCancelled}
        onSyncCancelledPickLists={syncCancelledPickLists}
        expiryDays={expiryDays}
      />
      <ExpiryConfigDialog
        open={expiryDialogOpen}
        onClose={() => setExpiryDialogOpen(false)}
        currentDays={expiryDays}
        onSave={saveExpiryDays}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full md:w-[640px]">
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk Upload</TabsTrigger>
          <TabsTrigger value="pick-lists" data-testid="tab-pick-lists">Pick Lists & QA</TabsTrigger>
          <TabsTrigger value="procurement" data-testid="tab-procurement">Procurement</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <OrdersListTab orders={orders} loading={loading} onOpen={setSelected}
            onExportShortages={exportShortages}
            onExportXlsx={openExportDialog}
            onCancel={cancelOrder} cancellingId={cancellingId}
            onSettle={openSettleDialog} settlingId={settlingId}
            startDate={startDate} endDate={endDate} sourceFilter={sourceFilter}
            onStartDate={setStartDate} onEndDate={setEndDate}
            onSourceFilter={setSourceFilter}
            filter={listFilter} setFilter={setListFilter}
            onClearDates={() => { setStartDate(''); setEndDate(''); setSourceFilter('all'); }} />
        </TabsContent>
        <TabsContent value="bulk" className="mt-4">
          <BulkOrderUploadTab onImported={onImported} />
        </TabsContent>
        <TabsContent value="pick-lists" className="mt-4">
          <PickListsTab onRefresh={fetchAll} />
        </TabsContent>
        <TabsContent value="procurement" className="mt-4">
          <ProcurementDashboardTab />
        </TabsContent>
      </Tabs>

      <OrderDetailDialog order={selected} open={!!selected}
        onClose={() => setSelected(null)}
        onCancel={cancelOrder} onCancelLine={cancelLine}
        cancellingId={cancellingId}
        cancellingLineUid={cancellingLineUid} />

      {/* Settle / Mark Paid dialog — fires the OG payment-status webhook so
          the retailer's available credit is restored on the OG App side. */}
      <Dialog open={!!settleDialog} onOpenChange={(o) => { if (!o && !settlingId) setSettleDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="settle-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Mark Order Paid
            </DialogTitle>
            <DialogDescription>
              {settleDialog
                ? <>Settle credit order <span className="font-mono font-semibold">{settleDialog.order_number}</span>.
                  The OG Retailer App will be notified instantly to restore the retailer&apos;s available credit by the
                  paid amount.</>
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Paid Amount (₹)</label>
              <Input
                type="number" min="0" step="0.01"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                placeholder="0.00"
                data-testid="settle-amount-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Defaults to order total. Use a smaller number for partial payment (call again later for the balance).
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Note (optional)</label>
              <Input
                value={settleNote}
                onChange={(e) => setSettleNote(e.target.value)}
                placeholder="e.g. UPI ref XYZ, cheque #1234"
                data-testid="settle-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleDialog(null)}
              disabled={!!settlingId}
              data-testid="settle-cancel-btn">Cancel</Button>
            <Button onClick={confirmSettle} disabled={!!settlingId}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="settle-confirm-btn">
              {settlingId
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Mark Paid & Restore Credit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportOpen} onOpenChange={(o) => { if (!exportBusy) setExportOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="export-date-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              Export Franchise Orders
            </DialogTitle>
            <DialogDescription>
              Pick the date range you want in the Excel. Leave both blank to export every order.
              The current source filter ({sourceFilter === 'all' ? 'all sources' : sourceFilter})
              will be applied to the export.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">From</label>
                <Input
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                  max={exportEnd || undefined}
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">To</label>
                <Input
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                  min={exportStart || undefined}
                  data-testid="export-end-date"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm"
                onClick={() => {
                  const t = new Date();
                  const iso = t.toISOString().slice(0, 10);
                  setExportStart(iso);
                  setExportEnd(iso);
                }}
                data-testid="export-preset-today"
              >Today</Button>
              <Button type="button" variant="outline" size="sm"
                onClick={() => {
                  const t = new Date();
                  const end = t.toISOString().slice(0, 10);
                  const s = new Date(t.getTime() - 6 * 86400000).toISOString().slice(0, 10);
                  setExportStart(s);
                  setExportEnd(end);
                }}
                data-testid="export-preset-7d"
              >Last 7 days</Button>
              <Button type="button" variant="outline" size="sm"
                onClick={() => {
                  const t = new Date();
                  const first = new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
                  setExportStart(first);
                  setExportEnd(t.toISOString().slice(0, 10));
                }}
                data-testid="export-preset-mtd"
              >This month</Button>
              <Button type="button" variant="outline" size="sm"
                onClick={() => {
                  const t = new Date();
                  const lastMonthEnd = new Date(t.getFullYear(), t.getMonth(), 0);
                  const lastMonthStart = new Date(t.getFullYear(), t.getMonth() - 1, 1);
                  setExportStart(lastMonthStart.toISOString().slice(0, 10));
                  setExportEnd(lastMonthEnd.toISOString().slice(0, 10));
                }}
                data-testid="export-preset-last-month"
              >Last month</Button>
              <Button type="button" variant="ghost" size="sm"
                onClick={() => { setExportStart(''); setExportEnd(''); }}
                data-testid="export-preset-all"
              >All time</Button>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setExportOpen(false)} disabled={exportBusy}>
              Cancel
            </Button>
            <Button type="button" onClick={exportXlsx} disabled={exportBusy} data-testid="export-confirm-btn">
              {exportBusy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting…</>
              ) : (
                <><FileSpreadsheet className="w-4 h-4 mr-2" />Download Excel</>
              )}
            </Button>
            <CsvDownloadButton onDownload={exportXlsx} testId="export-confirm-csv-btn" size="default" label="Download CSV" disabled={exportBusy} />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
